import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Star, Heart, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Feedback() {
  const { id } = useParams();
  const [info, setInfo] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/feedback/${id}/info`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Feedback nicht verfügbar"));
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) { toast.error("Bitte eine Bewertung wählen"); return; }
    setBusy(true);
    try {
      await api.post(`/feedback/${id}`, { rating, comment, name, email });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Absenden");
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] p-6">
        <div className="max-w-md w-full bg-white rounded-3xl border p-8 text-center">
          <div className="text-6xl mb-4">🎈</div>
          <h1 className="font-display font-black text-2xl mb-2">Ups!</h1>
          <p className="text-sm text-[var(--text-muted)]">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6"
           style={{ background: "linear-gradient(135deg,#EB75B5,#d97706)" }}>
        <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8 text-center" data-testid="feedback-success">
          <div className="mx-auto h-16 w-16 rounded-full flex items-center justify-center mb-4"
               style={{ background: "#fce7f3" }}>
            <Heart size={28} className="text-[var(--pop-pink)]" />
          </div>
          <div className="overline mb-2">Danke!</div>
          <h1 className="font-display font-black text-3xl mb-3">Du bist der Knaller.</h1>
          <p className="text-sm text-[var(--text-muted)]">
            Danke für dein Feedback zu POPCRN. Wir feilen an jedem Korn.
          </p>
          <div className="mt-6 flex justify-center gap-1">
            {[...Array(rating)].map((_, i) => (
              <Star key={i} size={20} className="fill-[var(--pop-yellow)] text-[var(--pop-yellow)]" style={{ fill: "#EB75B5", color: "#EB75B5" }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 md:p-10"
         style={{ background: "linear-gradient(135deg,#EB75B5 0%, #f9a8d4 55%, #fef3c7 100%)" }}
         data-testid="page-feedback">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-6 text-white">
          <img src="/logos/logo-white.png" alt="POPCRN" className="h-8 w-auto" />
          <span className="overline text-white/70">Feedback</span>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-8">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={16} className="text-[var(--pop-pink)]" />
            <div className="overline">Deine Meinung zählt</div>
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl leading-tight mb-2">
            Wie war dein POPCRN?
          </h1>
          {info?.products?.length > 0 && (
            <p className="text-sm text-[var(--text-muted)] mb-6">
              Zu deiner Probe: <span className="font-semibold">{info.products.join(" · ")}</span>
            </p>
          )}

          <form onSubmit={submit} className="space-y-5">
            {/* Star rating */}
            <div>
              <Label className="overline">Bewertung</Label>
              <div className="mt-2 flex items-center gap-1" data-testid="rating-stars">
                {[1, 2, 3, 4, 5].map((n) => {
                  const on = (hover || rating) >= n;
                  return (
                    <button
                      key={n}
                      type="button"
                      data-testid={`star-${n}`}
                      onMouseEnter={() => setHover(n)}
                      onMouseLeave={() => setHover(0)}
                      onClick={() => setRating(n)}
                      className="p-1 transition-transform hover:scale-125"
                      aria-label={`${n} Sterne`}
                    >
                      <Star
                        size={40}
                        strokeWidth={1.5}
                        className={on ? "fill-[var(--pop-pink)] text-[var(--pop-pink)]" : "text-neutral-300"}
                      />
                    </button>
                  );
                })}
              </div>
              {rating > 0 && (
                <div className="mt-2 text-sm font-semibold text-[var(--pop-pink)]">
                  {["", "Ok, das üben wir noch", "Geht so", "Solid!", "Richtig lecker", "Absoluter Knaller!"][rating]}
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="comment" className="overline">Kommentar (optional)</Label>
              <Textarea
                id="comment"
                data-testid="input-comment"
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Was hat dir besonders geschmeckt? Was können wir besser machen?"
                className="mt-2 rounded-xl"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="name" className="overline">Name (optional)</Label>
                <Input id="name" data-testid="input-name" value={name} onChange={(e) => setName(e.target.value)}
                       className="mt-2 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="email" className="overline">Email (optional)</Label>
                <Input id="email" data-testid="input-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                       className="mt-2 rounded-xl" />
              </div>
            </div>

            <Button
              type="submit"
              disabled={busy || !rating}
              data-testid="btn-submit-feedback"
              className="w-full h-12 rounded-full bg-[var(--pop-pink)] text-white hover:bg-[var(--pop-yellow-dark)] font-bold text-base"
            >
              {busy ? "Sende…" : "Feedback absenden →"}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t border-neutral-100 text-center text-xs text-[var(--text-muted)]">
            POPCRN · #{info?.delivery_number} · Danke, dass du probierst!
          </div>
        </div>
      </div>
    </div>
  );
}
